import {
  differenceInCalendarDays,
  endOfMonth,
  endOfYear,
  format,
  isEqual,
  startOfDay,
  startOfMonth,
  startOfYear,
  subDays,
  subMonths,
  subYears,
} from 'date-fns';
import { CalendarIcon } from 'lucide-react';
import { HTMLAttributes, useId, useState } from 'react';
import { type DateRange } from 'react-day-picker';
import { useTranslation } from 'react-i18next';
import { Button } from '@/Domain/components/Shared/Button/Button';
import { Calendar } from '@/Domain/components/Shared/Calendar/Calendar';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/Domain/components/Shared/Popover/Popover';
import { cn } from '@/Domain/utils/cn';

export interface DatePreset {
  label: string;
  range: DateRange;
}

export interface DateRangePickerProps extends HTMLAttributes<HTMLDivElement> {
  initialRange?: DateRange;
  onRangeChange?: (range: DateRange | undefined) => void;
  showPresets?: boolean;
  numberOfMonths?: number;
  /** Caps the selectable window width, for tabs whose API clamps longer ranges. */
  maxRangeDays?: number;
}

const presetDayCount = (preset: DatePreset): number | null =>
  preset.range.from && preset.range.to
    ? differenceInCalendarDays(preset.range.to, preset.range.from) + 1
    : null;

const getDefaultPresets = (t: (key: string) => string): DatePreset[] => {
  const today = new Date();
  return [
    { label: t('today'), range: { from: today, to: today } },
    { label: t('yesterday'), range: { from: subDays(today, 1), to: subDays(today, 1) } },
    { label: t('last_7_days'), range: { from: subDays(today, 6), to: today } },
    { label: t('last_30_days'), range: { from: subDays(today, 29), to: today } },
    { label: t('month_to_date'), range: { from: startOfMonth(today), to: today } },
    {
      label: t('last_month'),
      range: { from: startOfMonth(subMonths(today, 1)), to: endOfMonth(subMonths(today, 1)) },
    },
    { label: t('year_to_date'), range: { from: startOfYear(today), to: today } },
    {
      label: t('last_year'),
      range: { from: startOfYear(subYears(today, 1)), to: endOfYear(subYears(today, 1)) },
    },
  ];
};

const getDefaultInitialRange = (): DateRange => {
  const today = new Date();
  return { from: subDays(today, 6), to: today };
};

export const DateRangePicker = ({
  className,
  initialRange,
  onRangeChange,
  showPresets = true,
  numberOfMonths = 2,
  maxRangeDays,
}: DateRangePickerProps) => {
  const { t } = useTranslation('common', { keyPrefix: 'date_picker' });
  const today = new Date();
  const maxHintId = useId();

  const presets = getDefaultPresets(t);
  const defaultRange = initialRange ?? getDefaultInitialRange();

  const [open, setOpen] = useState<boolean>(false);
  const [month, setMonth] = useState<Date>(today);
  const [appliedRange, setAppliedRange] = useState<DateRange | undefined>(defaultRange);
  const [workingRange, setWorkingRange] = useState<DateRange | undefined>(defaultRange);
  const [selectedPresetLabel, setSelectedPresetLabel] = useState<string | null>(
    initialRange ? null : t('last_7_days'),
  );

  const handleOpenChange = (isOpen: boolean) => {
    if (isOpen) {
      setWorkingRange(appliedRange);
    } else {
      setWorkingRange(appliedRange);
    }
    setOpen(isOpen);
  };

  const handleSelect = (selected: DateRange | undefined) => {
    setWorkingRange({ from: selected?.from, to: selected?.to });
    const matchedPreset = presets.find(
      (preset) =>
        preset.range.from &&
        preset.range.to &&
        selected?.from &&
        selected?.to &&
        isEqual(startOfDay(preset.range.from), startOfDay(selected.from)) &&
        isEqual(startOfDay(preset.range.to), startOfDay(selected.to)),
    );
    setSelectedPresetLabel(matchedPreset?.label || null);
  };

  const handlePresetClick = (preset: DatePreset) => {
    setWorkingRange(preset.range);
    if (preset.range.from) {
      setMonth(preset.range.from);
    }
    setSelectedPresetLabel(preset.label);
  };

  // An over-cap drag leaves `to` unset (react-day-picker restarts the range), and consumers drop
  // a half range — so applying one would relabel the trigger while the data stayed put.
  const isWorkingRangeComplete = !!workingRange?.from && !!workingRange?.to;

  const handleApply = () => {
    if (!isWorkingRangeComplete) {
      return;
    }
    setAppliedRange(workingRange);
    onRangeChange?.(workingRange);
    setOpen(false);
  };

  const handleReset = () => {
    setWorkingRange(defaultRange);
    setSelectedPresetLabel(initialRange ? null : t('last_7_days'));
    if (defaultRange.from) {
      setMonth(defaultRange.from);
    }
  };

  const displayValue = appliedRange?.from ? (
    appliedRange.to ? (
      <>
        {format(appliedRange.from, 'LLL dd, y')} – {format(appliedRange.to, 'LLL dd, y')}
      </>
    ) : (
      format(appliedRange.from, 'LLL dd, y')
    )
  ) : (
    <span className="text-muted-foreground">{t('pick_a_date_range')}</span>
  );

  return (
    <div className={cn('', className)}>
      <Popover open={open} onOpenChange={handleOpenChange}>
        <PopoverTrigger asChild>
          <Button
            size="sm"
            variant="secondary"
            className={cn('justify-start font-normal min-w-[240px]')}
          >
            <CalendarIcon className="h-4 w-4 mr-2" />
            {displayValue}
          </Button>
        </PopoverTrigger>

        <PopoverContent
          className="w-auto p-0 max-h-[85vh] overflow-y-auto" /* prevent overflow on small/mobile screens */
          align="end"
          side="bottom"
          collisionPadding={8}
          sideOffset={4}
        >
          <div className="flex max-sm:flex-col">
            {showPresets && (
              <div className="relative border-border max-sm:order-1 max-sm:border-t sm:w-32">
                <div className="h-full border-border sm:border-e py-2">
                  <div className="flex flex-col px-2 gap-[2px]">
                    {presets.map((preset) => {
                      const dayCount = presetDayCount(preset);
                      const exceedsMax = !!maxRangeDays && !!dayCount && dayCount > maxRangeDays;
                      return (
                        <Button
                          key={preset.label}
                          type="button"
                          variant="ghost"
                          size="sm"
                          disabled={exceedsMax}
                          aria-describedby={exceedsMax ? maxHintId : undefined}
                          className={cn(
                            'h-8 w-full justify-start text-xs text-foreground',
                            selectedPresetLabel === preset.label && 'bg-accent',
                          )}
                          onClick={() => handlePresetClick(preset)}
                        >
                          {preset.label}
                        </Button>
                      );
                    })}
                  </div>
                  {!!maxRangeDays && (
                    <p
                      id={maxHintId}
                      data-testid="date-range-max-hint"
                      className="px-4 pt-2 text-xs text-muted-foreground"
                    >
                      {t('max_range_days', { days: maxRangeDays })}
                    </p>
                  )}
                </div>
              </div>
            )}
            <Calendar
              mode="range"
              month={month}
              onMonthChange={setMonth}
              showOutsideDays={false}
              selected={workingRange}
              onSelect={handleSelect}
              // react-day-picker rejects on differenceInCalendarDays(to, from) > max, which excludes
              // the first day, so the cap has to be one less than the inclusive day count.
              max={maxRangeDays ? maxRangeDays - 1 : undefined}
              numberOfMonths={numberOfMonths}
              className="p-3"
            />
          </div>
          <div className="flex items-center justify-end gap-1.5 border-t border-border p-3">
            <Button variant="secondary" size="sm" onClick={handleReset}>
              {t('reset')}
            </Button>
            <Button
              size="sm"
              disabled={!isWorkingRangeComplete}
              data-testid="date-range-apply"
              onClick={handleApply}
            >
              {t('apply')}
            </Button>
          </div>
        </PopoverContent>
      </Popover>
    </div>
  );
};
